import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useFocusEffect } from '@react-navigation/native';
import { FlashList } from '@shopify/flash-list';
import { useAppSelector } from 'store/hooks';
import { Button, CurrencyAmountText, rem, SettingsItemNavigate, Spacer } from 'design-system';
import IconArchive from 'design-system/icons/IconArchive';
import IconCircle from 'design-system/icons/IconCircle';
import IconPlus from 'design-system/icons/IconPlus';
import SectionHeader from 'design-system/SectionHeader';
import Text from 'design-system/Text';
import { WEB_SCREEN_BOTTOM_PADDING } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import useStatusBar from 'hooks/useStatusBar';
import useStyles from 'hooks/useStyles';
import TopupPendingWarning from 'features/invest/components/TopupPendingWarning';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { isWeb } from '../../../constants';
import usePots from '../hooks/usePots';
import { selectAddMoneyPotsSortedByBalance, selectPotFlashListData } from '../reducers/selectors';
import AutosaveEntryCard from './AutosaveEntryCard';
import FSCSCard from './FSCSCard';
import InitialDepositSection from './InitialDepositSection';
import OpenInvestAccountCTA from './OpenInvestAccountCTA';
import Pot from './Pot';
import PotListSectionHeader from './PotListSectionHeader';
const strings = {
    withdraw: 'Withdraw',
    fund: 'Deposit',
    yourPots: 'Your pots',
    archivedPots: 'Archived pots',
};
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.id;
const Pots = ({ onScroll, potsScrollViewRef }) => {
    useStatusBar('dark-content', false);
    const navigation = useMainStackNavigation();
    const connectedAccount = useConnectedAccount();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMemo(() => (isWeb ? { ...paddingHorizontalStyle, paddingBottom: WEB_SCREEN_BOTTOM_PADDING } : paddingHorizontalStyle), [paddingHorizontalStyle]);
    const addMoneyPots = useAppSelector(selectAddMoneyPotsSortedByBalance);
    const potFlashListData = useAppSelector(selectPotFlashListData);
    const archivedPots = useAppSelector((state) => state.pots.deletedPots);
    const { potsBalance, getUserPots } = usePots();
    const balance = useMemo(() => ({
        amount: potsBalance.value,
        currency: 'GBP',
    }), [potsBalance]);
    useFocusEffect(useCallback(() => {
        getUserPots();
    }, [getUserPots]));
    const handleCreatePot = useCallback(() => {
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Create a Pot')) {
            return;
        }
        navigation.navigateDeprecated('SelectPotType');
    }, []);
    const handleAddMoney = useCallback(() => {
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Add money')) {
            return;
        }
        const pot = addMoneyPots[0];
        if (!pot) {
            Modal.showConfirmation({
                title: 'You don’t have any pots yet',
                subtitle: 'Create a pot first, then add your money to it',
                cancelButtonTitle: 'Create new pot',
                confirmButtonTitle: 'Not now',
                onPressCancel: () => {
                    navigation.navigate('SelectPotType');
                },
                onConfirm: () => {
                    Modal.hide();
                },
            });
            return;
        }
        if (connectedAccount) {
            navigation.navigate('DepositToPotScreen', { pot, isAddMoneyFlow: true });
        }
        else {
            navigation.navigate('SelectFundingAccount', {
                pot,
            });
        }
    }, [addMoneyPots, connectedAccount]);
    const ListHeaderComponent = useMemo(() => (<>
        <Spacer h={32}/>
        <View style={styles.balanceContainer}>
          <Text Text-14 centered Secondary>
            Total savings
          </Text>
          <CurrencyAmountText size="L" value={balance}/>
        </View>
        <Spacer h={16}/>
        <Button hug brand small title="Add money" onPress={handleAddMoney} leftIconContainerStyle={styles.leftIconContainerStyle} leftIcon={<IconPlus width={rem(10)} height={rem(10)} color={colors.elements.white}/>}/>
        <Spacer h={32}/>
        <TopupPendingWarning />
        <AutosaveEntryCard />
        <Spacer h={16}/>
        <InitialDepositSection />
        <SectionHeader horizontalMargins>{strings.yourPots}</SectionHeader>
        <Spacer h={16}/>
        <TouchableBounceCard onDark noMarginTop noMarginBottom onPress={handleCreatePot} withPadding style={styles.container}>
          <IconCircle width={44} height={44}>
            <IconPlus color={colors.text.brand}/>
          </IconCircle>
          <Spacer.W16 />
          <Text Text-16>{'Create new pot'}</Text>
        </TouchableBounceCard>
      </>), [
        balance,
        colors.elements.white,
        colors.text.brand,
        handleAddMoney,
        handleCreatePot,
        styles.balanceContainer,
        styles.container,
        styles.leftIconContainerStyle,
    ]);
    const onOpenArchivedPots = useCallback(() => navigation.navigate('ArchivedPots'), []);
    const ListFooterComponent = useMemo(() => (<>
        <Spacer.H16 />
        <OpenInvestAccountCTA />
        {archivedPots && archivedPots?.length > 0 ? (<>
            <Spacer.H8 />
            <SectionHeader horizontalMargins>{strings.archivedPots}</SectionHeader>
            <Spacer.H16 />
            <TouchableBounceCard onDark onPress={onOpenArchivedPots} noMarginBottom noMarginTop>
              <View pointerEvents="none" style={styles.archivedPotView}>
                <SettingsItemNavigate title={`${archivedPots?.length} archived pot${archivedPots?.length === 1 ? '' : 's'}`} icon={<IconArchive color={colors.elements.secondary}/>}/>
              </View>
            </TouchableBounceCard>
          </>) : null}
        <FSCSCard text="Interest pots are FSCS-protected on eligible deposits. "/>
      </>), [archivedPots, onOpenArchivedPots, styles.archivedPotView, colors.elements.secondary]);
    const renderItem = useCallback(({ item }) => {
        if ('sectionTitle' in item) {
            return (<PotListSectionHeader title={item.sectionTitle} totalBalance={item.totalBalance} interestRate={item.interestRate}/>);
        }
        return <Pot data={item} isFirstInSection={item.isFirstInSection} isLastInSection={item.isLastInSection}/>;
    }, []);
    return (<View testID="PotsMainView" style={styles.mainContainer}>
      <AnimatedFlashList onScroll={onScroll} ref={potsScrollViewRef} data={potFlashListData} scrollEventThrottle={16} renderItem={renderItem} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} ListFooterComponent={ListFooterComponent} contentContainerStyle={contentContainerStyle} maintainVisibleContentPosition={MAINTAIN_VISIBLE_CONTENT_POSITION}/>
    </View>);
};
// On web the list header grows after the first render (balance, Autosave card), and
// FlashList's default keeps the first pot in place, so the tab opened scrolled past the header.
const MAINTAIN_VISIBLE_CONTENT_POSITION = isWeb ? { disabled: true } : undefined;
const styleSet = createStyleSheets(() => ({
    mainContainer: {
        flex: 1,
    },
    titleContainer: {
        flex: 1,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    balanceContainer: {
        alignItems: 'center',
    },
    container: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    leftIconContainerStyle: {
        paddingRight: rem(8),
    },
    archivedPotView: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
}));
export default memo(Pots);
